import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  IconButton,
  InputBase,
  Paper,
  CircularProgress,
  Alert,
  Tooltip,
} from "@mui/material";
import {
  Send as SendIcon,
  Image as ImageIcon,
  Close as CloseIcon,
} from "@mui/icons-material";

interface ChatInputProps {
  onSendMessage: (
    content: string,
    messageType: "text" | "image",
    file?: File,
  ) => Promise<void>;
  disabled?: boolean;
  uploading?: boolean;
  maxFileSize?: number; // in MB
}

const ChatInput: React.FC<ChatInputProps> = ({
  onSendMessage,
  disabled = false,
  uploading = false,
  maxFileSize = 5,
}) => {
  const [message, setMessage] = useState("");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      setError(null);

      if (!file) return;

      // Validate file type
      if (!file.type.startsWith("image/")) {
        setError("Please select an image file");
        return;
      }

      // Validate file size
      const fileSizeMB = file.size / (1024 * 1024);
      if (fileSizeMB > maxFileSize) {
        setError(`File size must be less than ${maxFileSize}MB`);
        return;
      }

      setSelectedFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    },
    [maxFileSize],
  );

  const handleRemoveFile = useCallback(() => {
    setSelectedFile(null);
    setPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  const handleSendMessage = useCallback(async () => {
    if (!message.trim() && !selectedFile) {
      setError("Please enter a message or select an image");
      return;
    }

    setSending(true);
    setError(null);

    try {
      if (selectedFile) {
        await onSendMessage(message, "image", selectedFile);
        handleRemoveFile();
      } else {
        await onSendMessage(message.trim(), "text");
      }
      setMessage("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send message");
    } finally {
      setSending(false);
    }
  }, [message, selectedFile, onSendMessage, handleRemoveFile]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (
      event.key === "Enter" &&
      !event.shiftKey &&
      !disabled &&
      !sending &&
      !uploading
    ) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const isDisabled = disabled || sending || uploading;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {preview && selectedFile && (
        <Box
          sx={{
            position: "relative",
            width: "100px",
            height: "100px",
            mx: 2,
            borderRadius: 1,
            overflow: "hidden",
            bgcolor: "action.hover",
          }}
        >
          <Box
            component="img"
            src={preview}
            alt="Preview"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
          <IconButton
            size="small"
            onClick={handleRemoveFile}
            disabled={isDisabled}
            sx={{
              position: "absolute",
              top: 0,
              right: 0,
              bgcolor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              "&:hover": {
                bgcolor: "rgba(0, 0, 0, 0.7)",
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Paper
        sx={{
          display: "flex",
          alignItems: "flex-end",
          gap: 1,
          p: 1,
          bgcolor: "background.paper",
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <Tooltip title="Upload image">
          <span>
            <IconButton
              size="small"
              onClick={() => fileInputRef.current?.click()}
              disabled={isDisabled || !!selectedFile}
              sx={{ color: "primary.main" }}
            >
              {uploading ? <CircularProgress size={24} /> : <ImageIcon />}
            </IconButton>
          </span>
        </Tooltip>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          hidden
          onChange={handleFileSelect}
        />

        <InputBase
          fullWidth
          multiline
          maxRows={3}
          placeholder="Type a message... (Shift+Enter for new line)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          disabled={isDisabled}
          sx={{
            px: 1,
            flex: 1,
          }}
        />

        <Tooltip title="Send message">
          <span>
            <IconButton
              size="small"
              onClick={handleSendMessage}
              disabled={isDisabled || (!message.trim() && !selectedFile)}
              sx={{ color: "primary.main" }}
            >
              {sending ? <CircularProgress size={24} /> : <SendIcon />}
            </IconButton>
          </span>
        </Tooltip>
      </Paper>
    </Box>
  );
};

export default ChatInput;
